import { useEffect, useRef, type ReactNode } from "react";
import { processMapSvg, type ProcessMapGraph } from "@gk/shared";

const STATUS: Record<string, { label: string; tone: string }> = {
  planned: { label: "Planned", tone: "" },
  live: { label: "Live", tone: "ochre live" },
  processing: { label: "Processing", tone: "blue" },
  draft: { label: "Draft", tone: "amber" },
  reviewed: { label: "Reviewed", tone: "blue" },
  verified: { label: "Verified", tone: "green" },
  cancelled: { label: "Cancelled", tone: "" },
  failed: { label: "Needs attention", tone: "red" },
};

export function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, tone: "" };
  return (
    <span className={`pill ${s.tone}`}>
      <span className="dot" />
      {s.label}
    </span>
  );
}

const SEV: Record<string, string> = { critical: "red", high: "red", medium: "amber", low: "blue" };
export function SeverityPill({ severity }: { severity: string | null }) {
  if (!severity) return null;
  return <span className={`pill ${SEV[severity] ?? ""}`}>{severity[0]!.toUpperCase() + severity.slice(1)}</span>;
}

/**
 * The swimlane process map. The SVG comes from the shared renderer (the same
 * one the PDF uses); every text value in it is escaped there.
 */
export function ProcessMap({ graph }: { graph: ProcessMapGraph }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = processMapSvg(graph);
  }, [graph]);
  return <div className="map-wrap" ref={ref} role="figure" aria-label={graph.title} />;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: "fixed", inset: 0, background: "rgba(20,35,28,0.38)", display: "grid", placeItems: "center", zIndex: 50, padding: 16 }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="panel" style={{ width: "min(560px, 100%)", maxHeight: "90vh", overflow: "auto", boxShadow: "var(--shadow)" }}>
        <div className="row" style={{ marginBottom: 16 }}>
          <h3>{title}</h3>
          <span className="spacer" />
          <button className="btn ghost small" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ErrorNote({ error }: { error: Error | null | undefined }) {
  if (!error) return null;
  return (
    <div className="error-box" role="alert">
      {error.message}
    </div>
  );
}
